import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import { useGetGettingStartedQuery } from '../../store/endpoints/GettingStartedEndpoints';
import { IGettingStartedStep } from '../../models/GettingStarted';

// Shared read model for the Getting Started checklist.
//
// Both the nav bar (to decide whether the entry point exists at all) and the button itself call
// this. They share one RTK Query subscription, so there is still only one request in flight.

/** Quiet interval. Nothing is running server side, so this is just a freshness check. */
export const GETTING_STARTED_POLL_IDLE = 60000;
/** Live interval while a background component is working, mirroring TasksDropdown's behaviour. */
export const GETTING_STARTED_POLL_ACTIVE = 5000;

export interface IGettingStartedState {
    steps: IGettingStartedStep[];
    completeCount: number;
    total: number;
    allComplete: boolean;
    /** True while any step has server-side work running. Drives the faster poll. */
    isProcessing: boolean;
    /** True while any step is waiting on the user to look at finished background work. */
    needsReview: boolean;
    isLoading: boolean;
    /**
     * False when the endpoint is missing, errored, or returned no steps. The nav entry point must
     * disappear in that case: an undeployed endpoint cannot be allowed to break the command bar.
     */
    isVisible: boolean;
}

export const useGettingStarted = (): IGettingStartedState => {
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [pollingInterval, setPollingInterval] = React.useState(GETTING_STARTED_POLL_IDLE);

    const query = useGetGettingStartedQuery(undefined, {
        pollingInterval,
        skip: !selectedOrganization
    });

    const steps = query.data?.steps ?? [];
    const isProcessing = steps.some(s => s.status === 'processing');

    React.useEffect(() => {
        setPollingInterval(isProcessing ? GETTING_STARTED_POLL_ACTIVE : GETTING_STARTED_POLL_IDLE);
    }, [isProcessing]);

    const completeCount = steps.filter(s => s.status === 'complete').length;

    return {
        steps,
        completeCount,
        total: steps.length,
        allComplete: steps.length > 0 && completeCount === steps.length,
        isProcessing,
        needsReview: steps.some(s => s.status === 'needsReview'),
        isLoading: query.isLoading,
        isVisible: !query.isError && steps.length > 0,
    };
};

export default useGettingStarted;
